<script setup lang="ts">
import { collection, query, where, Timestamp } from 'firebase/firestore'
import { signInWithCustomToken } from 'firebase/auth'
import { getAuth } from '../helpers/firebase'
import { onMounted, onUnmounted, ref, computed, watch } from 'vue'
import type { Ref } from 'vue'
import { BundleModelFactory } from 'monocle-models'
import { useCollection, useFirestore, useCurrentUser } from 'vuefire'
import { getStrings } from './pickerLanguage'
import { filterBundles, OrderBy } from './SearchAndFilter.vue'
import ConnectToPrimeApp from './ConnectToPrimeApp.vue'
import ShareModal from './ShareModal.vue'
import SearchAndFilter from './SearchAndFilter.vue'
import BundleItem from './BundleItem.vue'

const auth = getAuth()

const { bundleConverter } = new BundleModelFactory(Timestamp)

const error = ref<{
  type: 'auth' | 'api' | 'bundles' | 'generic'
  message?: string
} | null>(null)

const showLinkingDialog = ref(false)
const isInitializing = ref(false)
let linkAccountPollInterval: number | null = null

const props = defineProps({
  userToken: {
    type: String,
  },
  language: {
    type: String,
    required: true,
  },
  partnerId: {
    type: String,
    required: true,
  },
  darkMode: {
    type: Boolean,
    default: false,
  },
  bundleType: {
    type: [String, Array],
    default: 'object_scan',
    validator: (value: string | string[]) => {
      const validTypes = ['all', 'object_scan', 'monocle_video', 'prime_job']
      if (typeof value === 'string') {
        return validTypes.includes(value)
      }
      if (Array.isArray(value)) {
        return (
          value.every((type) => validTypes.includes(type)) && value.length > 0
        )
      }
      return false
    },
  },
})
const languageStrings = computed(() => {
  return getStrings(props.language)
})

const shortLoginCode = ref('')

const currentBundleId = ref('')

const linkAccountUrl = ref('')

const currentUser = useCurrentUser() ?? ref(null)

const q = computed(() => {
  const db = useFirestore()
  const r = collection(db, 'bundles').withConverter(bundleConverter)

  const queryConstraints = [
    where('metadata.owner', '==', currentUser.value?.uid ?? ''),
  ]

  // Handle bundle type filtering
  const bundleTypes = Array.isArray(props.bundleType)
    ? props.bundleType
    : [props.bundleType]

  // Add bundle type filter if not 'all'
  if (!bundleTypes.includes('all')) {
    if (bundleTypes.length === 1) {
      // Single type filter
      queryConstraints.push(where('metadata.data_type', '==', bundleTypes[0]))
    } else {
      // Multiple types filter using 'in' operator (max 10 values)
      queryConstraints.push(where('metadata.data_type', 'in', bundleTypes))
    }
  }

  const q = query(r, ...queryConstraints)
  return q
})

const bundles = useCollection(q)

const input = <Ref<HTMLInputElement | null>>ref(null)

const initialize = async () => {
  if (isInitializing.value) return

  isInitializing.value = true

  try {
    if (!props.userToken) {
      error.value = { type: 'auth', message: 'No user token provided' }
      return
    }

    const decodedUserToken = atob(props.userToken)
    const userTokenObject = JSON.parse(decodedUserToken)

    console.log('userTokenObject', userTokenObject)

    if (userTokenObject.error) {
      error.value = { type: 'auth', message: userTokenObject.error }
      return
    }

    const shortcode = userTokenObject.shortcode

    shortLoginCode.value = shortcode
    linkAccountUrl.value = `${import.meta.env.VITE_GALLERY_URL}/link-account?shortcode=${shortcode}`

    //use the short login code to get the firebase auth token

    const firebaseAuthTokenResponse = await fetch(
      `${import.meta.env.VITE_GALLERY_URL}/api/short-login-code`,
      {
        method: 'POST',
        body: JSON.stringify({ code: shortcode, partner: props.partnerId }),
        mode: 'cors',
      },
    )

    const responseObject = firebaseAuthTokenResponse.json()

    const token = (await responseObject).authToken

    if (token) {
      const { user } = await signInWithCustomToken(auth, token)
      currentUser.value = user
      showLinkingDialog.value = false
      error.value = null
    } else {
      showLinkingDialog.value = true
    }
  } catch (e) {
    error.value = {
      type: 'generic',
      message: e instanceof Error ? e.message : String(e),
    }
  } finally {
    isInitializing.value = false
  }
}

const refreshAfterLinkAccount = async () => {
  if (showLinkingDialog.value) {
    await initialize()
  }
}

onMounted(async () => {
  await initialize()

  window.addEventListener('focus', refreshAfterLinkAccount)
  linkAccountPollInterval = window.setInterval(refreshAfterLinkAccount, 3000)
})

onUnmounted(() => {
  window.removeEventListener('focus', refreshAfterLinkAccount)
  if (linkAccountPollInterval) {
    clearInterval(linkAccountPollInterval)
    linkAccountPollInterval = null
  }
})

const appLoginLink = (shortLoginCode: string) => {
  return `https://prime.monocle3d.com/login?shortLoginCode=${shortLoginCode}`
}

const loginLink = computed(() => {
  if (!props.userToken) return ''
  return appLoginLink(shortLoginCode.value)
})

const setCurrentBundleId = (id: string) => {
  currentBundleId.value = id
}

const selectBundleForCustomization = (id: string) => {
  setCurrentBundleId(id)
  isCustomizePopupVisible.value = true
}

// Function to explicitly dispatch the bundle-id-set event
const dispatchPickerConfirmEvent = (bundleId: string) => {
  if (input.value) {
    input.value.value = bundleId
    input.value.dispatchEvent(
      new CustomEvent('picker-confirm', {
        bubbles: true,
        composed: true,
        detail: { bundleId },
      }),
    )
  }
}

// Function to dispatch the picker-close event
const dispatchPickerCloseEvent = () => {
  if (input.value) {
    input.value.dispatchEvent(
      new CustomEvent('picker-close', {
        bubbles: true,
        composed: true,
      }),
    )
  }
}
const currentBundle = computed(() => {
  if (!bundles.value || !currentBundleId.value) return null
  else {
    return bundles.value?.find((b) => b.id === currentBundleId.value)
  }
})

const isConnectPopupVisible = ref(false)
const isCustomizePopupVisible = ref(false)
const hasAutomaticallyOpenedConnectPopup = ref(false)
const isPopupActive = computed(
  () => isConnectPopupVisible.value || isCustomizePopupVisible.value,
)

watch(
  [bundles, bundles.pending, currentUser, isInitializing, showLinkingDialog],
  ([loadedBundles, bundlesPending, user, initializing, linkingAccount]) => {
    if (
      !hasAutomaticallyOpenedConnectPopup.value &&
      !initializing &&
      !linkingAccount &&
      user &&
      !bundlesPending &&
      loadedBundles.length === 0
    ) {
      isConnectPopupVisible.value = true
      hasAutomaticallyOpenedConnectPopup.value = true
    }
  },
)

const toggleConnectPopup = () => {
  isConnectPopupVisible.value = !isConnectPopupVisible.value
}

const toggleCustomizeModel = () => {
  isCustomizePopupVisible.value = !isCustomizePopupVisible.value
}

const orderBy = ref<OrderBy>(OrderBy.Modified)
const orderAscending = ref(false)
const searchTerm = ref('')

const filteredBundles = computed(() => {
  if (!bundles.value) return []
  return filterBundles(
    bundles.value,
    orderBy.value,
    orderAscending.value,
    searchTerm.value,
  )
})
</script>

<template>
  <div
    class="main-container"
    :class="darkMode ? 'dark-mode' : 'light-mode'">
    <div
      v-if="error"
      class="error-container">
      <div class="error-content">
        <h2>Sorry, there was an error</h2>
        <p>Please contact support</p>
      </div>
    </div>
    <div
      v-else
      class="no-error">
      <!---POPUP STUFF-->
      <Transition name="fade">
        <div
          class="popup-background"
          v-if="isPopupActive">
          <ConnectToPrimeApp
            :loginLink="loginLink"
            v-if="isConnectPopupVisible"
            :language="language"
            @close="toggleConnectPopup" />
          <ShareModal
            :bundle="currentBundle"
            :currentBundleId="currentBundleId"
            :language="language"
            :shortcode="shortLoginCode"
            v-if="isCustomizePopupVisible && currentBundle && shortLoginCode"
            @close="toggleCustomizeModel"
            @confirm="dispatchPickerConfirmEvent" />
          <!-- <ModelCustomizer
          v-if="isCustomizePopupVisible"
          :currentBundleId="currentBundleId"
          :language="language"
          @close="toggleCustomizeModel"
          @resetCustomSettings="resetCustomSettings" /> -->
        </div>
      </Transition>

      <!-- Account Linking Flow - Hide everything else when this is active -->
      <div
        v-if="showLinkingDialog"
        class="link-account-wrapper">
        <p>
          {{ languageStrings.link_account }}
        </p>
        <a
          :href="linkAccountUrl"
          target="_blank"
          role="button"
          >{{ languageStrings.link_account_button }}</a
        >
      </div>

      <!-- Main Picker Interface - Only show when not linking account -->
      <template v-else>
        <div class="top-nav">
          <div class="top-bar-container">
            <!-- Close button - left -->
            <button
              class="close-button"
              @click="dispatchPickerCloseEvent">
              {{ languageStrings.cancel }}
            </button>

            <!-- SearchAndFilter - center -->
            <SearchAndFilter
              :language="language"
              v-model:search-term="searchTerm"
              v-model:order-by="orderBy"
              v-model:order-ascending="orderAscending"
              :enableDropdown="true" />

            <!-- Connect button - right -->
            <button
              class="connect-button"
              @click="toggleConnectPopup">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 24 24"
                class="connect-button-icon">
                <!-- Simplified QR code icon -->
                <rect
                  x="3"
                  y="3"
                  width="7"
                  height="7"
                  rx="1" />
                <rect
                  x="14"
                  y="3"
                  width="7"
                  height="7"
                  rx="1" />
                <rect
                  x="3"
                  y="14"
                  width="7"
                  height="7"
                  rx="1" />
                <rect
                  x="5"
                  y="5"
                  width="3"
                  height="3" />
                <rect
                  x="16"
                  y="5"
                  width="3"
                  height="3" />
                <rect
                  x="5"
                  y="16"
                  width="3"
                  height="3" />
                <rect
                  x="14"
                  y="12"
                  width="2"
                  height="2" />
                <rect
                  x="17"
                  y="12"
                  width="2"
                  height="2" />
                <rect
                  x="20"
                  y="12"
                  width="1"
                  height="2" />
                <rect
                  x="14"
                  y="15"
                  width="1"
                  height="2" />
                <rect
                  x="16"
                  y="15"
                  width="2"
                  height="2" />
                <rect
                  x="19"
                  y="15"
                  width="2"
                  height="2" />
                <rect
                  x="14"
                  y="18"
                  width="2"
                  height="2" />
                <rect
                  x="17"
                  y="18"
                  width="1"
                  height="2" />
                <rect
                  x="19"
                  y="18"
                  width="2"
                  height="2" />
              </svg>
              <span class="connect-button-label">
                {{ languageStrings.connect_button }}
              </span>
            </button>
          </div>
        </div>
        <div class="bundle-list">
          <div v-for="bundle in filteredBundles">
            <label class="bundleItem-card">
              <input
                @click="
                  () => {
                    selectBundleForCustomization(bundle.id)
                  }
                "
                type="radio"
                :key="bundle.id"
                :value="bundle.id"
                name="bundle.id"
                v-on:change="
                  () => {
                    setCurrentBundleId(bundle.id)
                  }
                " />
              <div class="card-visuals-wrapper">
                <BundleItem
                  :bundle="bundle"
                  :bundle_id="bundle.id"
                  :selected="currentBundleId === bundle.id"
                  @card-click="selectBundleForCustomization(bundle.id)" />
                <!-- <ModelOverlay
                  v-if="currentBundleId === bundle.id"
                  :bundle="bundle"
                  @openCustomizerPopup="customizeModel" /> -->
              </div>
            </label>
          </div>
        </div>
      </template>

      <input
        ref="input"
        type="text"
        name="currentBundleId"
        id="currentBundleId" />
    </div>
  </div>
</template>

<style>
@import '../assets/globals.css';
</style>

<style scoped>
.main-container {
  display: flex;
  position: relative;
  background-color: var(--background-color);
  width: 100%;
  height: 100%;
  flex-direction: column;
  max-height: 100%;
}

.no-error {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.top-nav {
  position: relative;
  flex-shrink: 0;
  z-index: 3;
}

.top-bar-container {
  display: grid;
  grid-template-columns: auto minmax(12rem, 400px) auto;
  justify-content: space-between;
  align-items: center;
  column-gap: 1rem;
  height: 52px;
  width: 100%;
  position: relative;
  background: linear-gradient(var(--background-gradient));
  padding: 0.25rem 1rem;
  box-sizing: border-box;
}

.top-bar-container :deep(.search-filter-container) {
  grid-column: 2;
  width: 100%;
  min-width: 180px;
}

.top-bar-container :deep(.content-wrapper) {
  min-width: 0;
}

.top-bar-container :deep(.search-input) {
  min-width: 0;
}
.bundle-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  overflow-y: auto;
  gap: 1rem;
  padding: 2rem;
  flex: 1;
}

.bundleItem-card {
  position: relative;
}

.card-visuals-wrapper {
  position: relative;
}

.title {
  position: absolute;
  bottom: 0;
  left: 1rem;
  margin-bottom: 0.25rem;
}

.connect-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid var(--background-reverse);
  color: var(--text-color);
  border-radius: 2rem;
  height: 36px;
  padding: 0 1rem;
  background: transparent;
  cursor: pointer;
  font-family: var(--title-font);
  font-size: 0.9rem;
  transition-duration: 0.2s;
  flex-shrink: 0;
  white-space: nowrap;
  justify-self: end;
}

.connect-button:hover {
  background-color: var(--primary-color);
  transition-duration: 0.2s;
}

.connect-button-icon {
  width: 1rem;
  height: 1rem;
  flex: 0 0 auto;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.popup-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  padding-top: 2rem;
  z-index: 4;
  background-color: #000000a7;
}

input {
  display: none;
}

[type='radio'] {
  display: none;
}

.qr-code {
  width: 400px;
  height: 400px;
}
.qr-code svg {
  width: 100%;
  height: 100%;
}

@media screen and (max-width: 1023px) {
  .top-bar-container {
    grid-template-columns: auto minmax(8rem, 400px) auto;
    column-gap: 0.5rem;
    padding: 1rem 0.5rem;
  }

  .top-bar-container :deep(.search-filter-container) {
    min-width: 0;
  }

  .connect-button {
    width: 36px;
    padding: 0;
    justify-content: center;
  }

  .connect-button-label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
  .bundleItem-card {
    margin: 1rem;
  }

  img {
    width: 240px;
  }
}

.link-account-wrapper {
  display: flex;
  align-content: center;
  justify-content: center;
  padding: 2rem;
  margin: auto;
  flex-wrap: wrap;
  width: 90%;
  max-width: 600px;
  min-height: calc(20vh);
  align-items: center;
  box-sizing: border-box;
}

.link-account-wrapper p {
  width: 100%;
  text-align: center;
}

.link-account-wrapper a {
  display: flex;
  border: 1px solid var(--background-reverse);
  text-decoration: none;
  color: var(--text-color);
  border-radius: 2rem;
  height: 44px;
  padding: 0rem 1.5rem;
  align-items: center;
  transition-duration: 0.2s;
}

.link-account-wrapper a:hover {
  background-color: var(--primary-color);
  transition-duration: 0.2s;
}

.error-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--background-color);
  z-index: 5;
}

.error-content {
  text-align: center;
  padding: 2rem;
  max-width: 400px;
}

.error-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 1rem;
  color: var(--text-color);
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1.5rem;
  border: 1px solid var(--background-reverse);
  border-radius: 2rem;
  background: transparent;
  color: var(--text-color);
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background-color: var(--primary-color);
}

.close-button {
  border: 1px solid var(--background-reverse);
  color: var(--text-color);
  border-radius: 2rem;
  height: 36px;
  padding: 0 1rem;
  background: transparent;
  cursor: pointer;
  font-family: var(--title-font);
  font-size: 0.9rem;
  transition-duration: 0.2s;
  flex-shrink: 0;
  white-space: nowrap;
  justify-self: start;
}

.close-button:hover {
  background-color: var(--primary-color);
  transition-duration: 0.2s;
}
</style>
